import { Api } from '@/api/api';
import { AssignUserRequest } from '@/api/requests/users/assign-user-request';
import { CreateUserRequest } from '@/api/requests/users/create-user-request';
import { UpdateUserRequest } from '@/api/requests/users/update-user-request';
import { UserRequestFilters } from '@/api/requests/users/user-request-filters';
import { Role } from '@/shared/roles/role';
import { User } from '@/shared/user';

export class UserApi extends Api {
    public getMeAsync(): Promise<User> {
        return this.getAsync<User>(`v1/user/me`);
    }

    public getUsersAsync(): Promise<Array<User>> {
        return this.getAsync<Array<User>>(`v1/user`);
    }

    public getLegalAdvisorsAsync(filters: UserRequestFilters): Promise<Array<User>> {
        return this.getAsync<Array<User>>(`v1/user/legalAdvisor`, filters);
    }

    public getAppointmentBookersAsync(filters: UserRequestFilters): Promise<Array<User>> {
        return this.getAsync<Array<User>>(`v1/user/booker`, filters);
    }

    public getInteractionCreatorsAsync(filters: UserRequestFilters): Promise<Array<User>> {
        return this.getAsync<Array<User>>(`v1/user/interactionCreator`, filters);
    }

    public getAssignedTaskUsersAsync(filters: UserRequestFilters): Promise<Array<User>> {
        return this.getAsync<Array<User>>(`v1/user/assignedTask`, filters);
    }

    public getCompletedTaskUsersAsync(filters: UserRequestFilters): Promise<Array<User>> {
        return this.getAsync<Array<User>>(`v1/user/completedTask`, filters);
    }

    public getAccountCreatorsAsync(filters: UserRequestFilters): Promise<Array<User>> {
        return this.getAsync<Array<User>>(`v1/user/accountCreator`, filters);
    }

    public getAssignedCallUsersAsync(filters: UserRequestFilters): Promise<Array<User>> {
        return this.getAsync<Array<User>>(`v1/user/assignedCall`, filters);
    }

    public getAccessedAccountUsersAsync(filters: UserRequestFilters): Promise<Array<User>> {
        return this.getAsync<Array<User>>(`v1/user/accessedAccount`, filters);
    }

    public getTargetUsersAsync(filters: UserRequestFilters): Promise<Array<User>> {
        return this.getAsync<Array<User>>(`v1/user/target`, filters);
    }

    public async updateUserAsync(request: UpdateUserRequest): Promise<void> {
        await this.putAsync(`v1/user/${request.id}`, request);
    }

    public async createUserAsync(request: CreateUserRequest): Promise<void> {
        await this.postAsync(`v1/user`, request);
    }

    public async deleteUserAsync(user: User): Promise<void> {
        await this.deleteAsync(`v1/user/${user.id}`);
    }

    public async reinviteUserAsync(user: User): Promise<void> {
        await this.postAsync(`v1/user/${user.id}/invite`, { userId: user.id, version: user.version });
    }

    public async assignToPartnerPracticeAsync(userId: string, request: AssignUserRequest): Promise<void> {
        await this.putAsync(`v1/user/${userId}/assign`, request);
    }

    public async unassignFromPartnerPracticeAsync(userId: string): Promise<void> {
        await this.putAsync(`v1/user/${userId}/unassign`, null);
    }

    public async getRolesAsync(): Promise<Array<Role>> {
        return await this.getAsync<Array<Role>>(`v1/user/role`);
    }

    public constructor() {
        super();
    }
}
